import styled, { css } from 'styled-components';
import { rem, rgba, isLight } from '@helpers/style';
import { breakpoint } from './mixins';

export const Main = styled.div`
	margin-top: 65px;

	${breakpoint.greaterThan('desktop')`
    margin-top: 80px;
  `}

	&:before {
		display: table;
		content: ' ';
	}
`;

export const Container = styled.div`
	margin-left: auto;
	margin-right: auto;

	${breakpoint.greaterThan('desktop')`
    width: 90%;
  `}

	${breakpoint.greaterThan('superlarge')`
    max-width: ${(p) => p.theme.device.xxl}px;
  `}
`;

export const ContainerFluid = styled.div`
	width: 100%;
	max-width: none;
	display: flex;
	flex-wrap: wrap;
`;

export const Tags = styled.ul`
	li {
		display: inline-block;
		padding: 4px 15px;
		border-radius: 100px;
		border: 1px solid #e4e4e4;
		font-size: 0.875rem;
		margin: 2px;
	}
`;

export const BackImage = styled.div`
	background-image: url(${(p) => p.url});
	background-size: cover;
	background-repeat: no-repeat;
	background-position: 50%;
	width: 100%;
	height: 100%;
	z-index: 2;
`;

export const Line = styled.div`
	border-top: 1px solid ${(p) => p.theme.color.grayDlight};
	margin: 20px 0;
	height: 1px;
`;

export const Badge = styled.span.attrs((p) => {
	const attrs = { $color: p.color, $pill: p.pill, $outline: p.outline };
	delete p.color;
	delete p.pill;
	delete p.outline;
	return attrs;
})`
	padding: 2px 8px;
	border-radius: ${(p) => (p.$pill ? 99 : p.theme.button.radius)}px;
	display: inline-block;
	font-size: ${(p) => rem(12, p.theme.fontSize.base)};
	text-align: center;

	${(p) =>
		p.$outline
			? css`
					border: 1px solid ${p.theme.color[p.$color]};
					color: ${p.theme.color[p.$color]};
			  `
			: css`
					color: ${p.$color && isLight(p.theme.color[p.$color])
						? '#333'
						: 'white'};
					background-color: ${p.theme.color[p.$color]};
			  `}
`;

export const StyledLightBox = styled.div`
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	height: 100vh;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.7);
	transition: ${(p) => p.theme.transition.all};
	visibility: visible;
	position: fixed;
	z-index: 1000;
`;

export const AnchorButton = styled.button`
	outline: 0;
	padding: 0;
	background: none;
	border: none;
`;

const spinnerSize = {
	sm: css`
		width: 20px;
		height: 20px;
	`,
	md: css`
		width: 35px;
		height: 35px;
	`,
	lg: css`
		width: 45px;
		height: 45px;
	`,
};

const spinnerColor = {
	white: css`
		border: 3px solid ${(p) => rgba(p.theme.color.white, 0.2)};
		border-left-color: ${(p) => p.theme.color.white};
	`,
	gray: css`
		border: 3px solid ${(p) => p.theme.color.graySuperlight};
		border-left-color: ${(p) => p.theme.color.grayMedium};
	`,
	secondary: css`
		border: 3px solid ${(p) => p.theme.color.graySuperlight};
		border-left-color: ${(p) => p.theme.color.grayMedium};
	`,
	primary: css`
		border: 3px solid ${(p) => p.theme.color.graySuperlight};
		border-left-color: ${(p) => p.theme.color.primary};
	`,
	success: css`
		border: 3px solid ${(p) => rgba(p.theme.color.white, 0.2)};
		border-left-color: ${(p) => p.theme.color.white};
	`,
};

export const Spinner = styled.strong.attrs((p) => ({
	$color: p.$color || 'primary',
	$size: p.$size || 'sm',
}))`
  vertical-align: middle;
  border-radius: 50%;
  position: relative;
  animation: loader 0.8s infinite linear;

  ${(p) =>
		p.$block
			? css`
					display: block;
					margin: ${p.$margin || 30}px auto;
			  `
			: css`
					display: inline-block;
					${p.$margin &&
					css`
						margin: ${p.$margin}px 0;
					`}
			  `}


  ${(p) => p.$size && spinnerSize[p.$size]}
  ${(p) => p.$color && spinnerColor[p.$color]}

  &:after {
    border-radius: 50%;
    width: 16px;
    height: 16px;
  }

  @keyframes loader {
    0% {
      transform: rotate(0deg);
      -o-transform: rotate(0deg);
    }

    50% {
      transform: rotate(360deg);
      -o-transform: rotate(360deg);
    }

    100% {
      transform: rotate(720deg);
      -o-transform: rotate(720deg);
    }
  }
`;

export const Error = styled.div`
	color: ${(p) => p.theme.color.danger};
`;

export const Message = styled.div`
	background-color: ${(p) => rgba(p.theme.color[p.$color], 0.2)};
	direction: rtl;
	border-radius: 10px;
	padding: 15px 20px;
	position: relative;
	width: 100%;

	${breakpoint.greaterThan('tablet')`
    ${(p) =>
			!p.$block &&
			css`
				max-width: 320px;
			`}
  `}

	display: table;
	margin: 0 auto 30px;
	font-size: 14px;

	.icon {
		float: left;
	}

	.button-holder {
		text-align: center;
		margin-top: 10px;

		${breakpoint.greaterThan('tablet')`
      position: absolute;
      left: 20px;
      top: 50%;
      transform: translateY(-50%);
      margin-top: 0;
    `}
	}
`;

export const CustomMain = styled.div`
  background-color: ${(p) => p.theme.color.bgBlue};
  min-height: 100vh;

  .main-content {
    padding-top: 80px;
    min-height: 700px;

    ${breakpoint.greaterThan('tablet')`
      padding-top: 100px;
    `}
  }
}`;

export const Wrapper = styled.div`
  width: 90%;
  max-width: 1400px;
  margin: 0 auto;
}`;

export const Box = styled.div`
  text-align: center;
  width: 100%;
  max-width: 450px;
  margin: 0 auto;

  ${(p) =>
		p.$white &&
		css`
			position: relative;
			background-color: ${p.theme.color.white};
			padding: 2.5rem;
			border-radius: 10px;
			box-shadow: 0 5px 10px ${rgba(p.theme.color.black, 0.2)};
			z-index: 2;
		`}

  hgroup {
    margin-bottom: 30px;

    h3 {
      margin-bottom: 10px;
    }
    h6 {
      margin: 0;
    }
  }

  .link {
    display: inline-block;
    margin-top: 20px;
  }
}`;

export const Form = styled.form``;

export const CreditBox = styled.span`
	background-color: ${(p) => rgba(p.theme.color.grayLight, 0.2)};
	border-radius: 99px;
	padding: 2px 10px;
  display:flex;
  margin: 0 5px;
	span {
		font-size: 12px;
		display: inline-block;
		direction: rtl;
		padding: 0 2px;
    text-wrap: nowrap;
	}

	.icon {
		font-size: 16px;
		padding: 0 2px;
	}
`;

export const UserPageHeader = styled.hgroup`
	margin-bottom: 40px;
	text-align: center;
`;

export const UserPageTitle = styled.h2`
	font-size: 22px;
`;

export const UserPageSubTitle = styled.h5`
	font-weight: normal;
	color: ${(p) => p.theme.color.grayMedium};
	font-size: 14px;
`;

export const Row = styled.div`
	width: 100%;
	float: right;
`;

export const Col = styled.div`
	${breakpoint.greaterThan('tablet')`
    width: 50%;
	  float: right;
	  padding: 0 10px;
  `}
`;

export const ButtonHolder = styled.div`
	float: left;
	width: 100%;
	margin-top: 40px;
	text-align: center;

	button,
	a {
		min-width: 200px;
	}
`;
